import React from 'react';
import { Button, Drawer, Loader } from 'freemium-ui';
import { compact, isEqual, isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import CustomFilterView from 'routes/screens/components/CustomFilterView';
import FilterViewActions from 'routes/screens/components/CustomFilterView/actions';
import FilterViewPopOvers from 'routes/screens/components/CustomFilterView/popovers';
import { DrawerMenuContainer, DrawerMenuList } from 'shared/styles';
import { ConditionType, FilterViewType } from '../../../../../shared/types';
import Icon from '../../../../../shared/components/Icon';
import { FilterViewContainer } from './styles';
import { DefaultFilterViews } from '../../constants';

type ContactsDefaultFilterViewProps = {
  filterViewCount?: number;
  onChange: (filterView: FilterViewType, refetch?: boolean) => void;
  onDelete: (filterId: string, filterName: string) => void;
  onSave: (filterView: FilterViewType) => void;
  filters: Record<string, ConditionType>;
  filteredViews: FilterViewType[];
  onClearFilterView: (selectedFilter: FilterViewType) => void;
  isFilterConditionsModified: boolean;
  setisFilterConditionsModified: (value: boolean) => void;
  selectedFilter: FilterViewType;
};

const FilterView = (props: ContactsDefaultFilterViewProps) => {
  const {
    onChange,
    filterViewCount,
    onDelete,
    filters,
    onSave,
    onClearFilterView,
    filteredViews,
    isFilterConditionsModified,
    setisFilterConditionsModified,
    selectedFilter,
  } = props;

  const { t } = useTranslation();
  const [filterViewName, setFilterViewName] = React.useState<string>('');
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [editFilterViewAnchor, setEditFilterViewAnchor] =
    React.useState<HTMLDivElement | null>(null);
  const editFilterViewRef: React.MutableRefObject<HTMLDivElement | null> =
    React.useRef<HTMLDivElement>(null);

  const [isDrawerOpen, toggleDrawer] = React.useState<boolean>(false);

  const saveFilterView = (filterViewName?: string, isCreate = false) => {
    const filteredConditions = filters && compact(Object.values(filters));
    const filterView = !isCreate ? selectedFilter : null;
    // the ternary condition is for checking whether it is for creating or updating a filter
    onSave({
      id:
        !filterViewName && !isCreate ? selectedFilter.id : filterView?.id ?? '',
      name: !filterViewName ? selectedFilter.name : filterViewName ?? '',
      conditions: filteredConditions || [],
      created_at: filterView?.created_at,
      updated_at: filterView?.updated_at,
    });
  };

  // first search in default views if not search in custom views
  React.useEffect(() => {
    const nonNullFilters = compact(Object.values(filters));
    if (
      !isEqual(selectedFilter.conditions, nonNullFilters) &&
      nonNullFilters.length
    ) {
      setisFilterConditionsModified(true);
    } else {
      setisFilterConditionsModified(false);
    }
  }, [selectedFilter, filters]);

  return (
    <FilterViewContainer isContact>
      <Button
        className="filter"
        inline
        type="secondary"
        onClick={() => toggleDrawer(true)}
      >
        <Icon width="13" height="13" glyph="filter" />
      </Button>
      <div
        className="filter-view-name"
        aria-describedby="filter-view-edit-popover"
      >
        <span>
          {!DefaultFilterViews.filter(
            (filter) => selectedFilter.id == filter.id,
          )
            ? selectedFilter.name
            : // we might face an exception if pre-existing key in translation is used as name here. Check for type at that time
              t(selectedFilter.name)}
        </span>
        {isNil(filterViewCount) ? (
          <Loader size="small" />
        ) : (
          <span>({filterViewCount || 0})</span>
        )}
        <div
          className="filter-view-popover-anchor"
          aria-describedby="filter-view-edit-popover"
          ref={editFilterViewRef}
        />
      </div>

      {isFilterConditionsModified && (
        <FilterViewActions
          onClearFilterView={onClearFilterView}
          onSave={() => saveFilterView(filterViewName)}
          selectedFilter={selectedFilter}
          filterViews={filteredViews}
          setAnchorEl={setAnchorEl}
        />
      )}
      <Drawer
        isOpen={isDrawerOpen}
        onDismiss={() => toggleDrawer(false)}
        size="small"
        placement="left"
        hideDismissIcon
        customClass="in-app-drawer"
      >
        <DrawerMenuContainer>
          <DrawerMenuList>
            <CustomFilterView
              onChange={onChange}
              onDelete={onDelete}
              toggleDrawer={toggleDrawer}
              selectedFilter={selectedFilter}
              filteredViews={filteredViews}
              editFilterViewRef={editFilterViewRef}
              setEditFilterViewAnchor={setEditFilterViewAnchor}
              setFilterViewName={setFilterViewName}
            />
            <div className="filter-view-heading">
              {t('responses.default_filter_views')}
            </div>
            {DefaultFilterViews.map((defaultFilter) => (
              <div
                className={`drawer-menu-item filter-view ${
                  defaultFilter.id === selectedFilter.id
                    ? 'selected-filter'
                    : ''
                }`.trim()}
                onClick={() => {
                  toggleDrawer(false);
                  onChange(defaultFilter);
                }}
                key={defaultFilter.id}
              >
                <span>{t(defaultFilter.name)}</span>
              </div>
            ))}
          </DrawerMenuList>
        </DrawerMenuContainer>
      </Drawer>

      <FilterViewPopOvers
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        filterViewName={filterViewName}
        setFilterViewName={setFilterViewName}
        saveFilterView={saveFilterView}
        filterViewEditAnchorEl={editFilterViewAnchor}
        setFilterViewEditAnchorEl={setEditFilterViewAnchor}
      />
    </FilterViewContainer>
  );
};

export default FilterView;
